/**
 * The gap between "the pack was accepted" and "the chat has something to show for it".
 *
 * Accepting a pack does not produce a reply — it dispatches a hidden SYSTEM message into a freshly
 * created conversation and returns. The maestro's walkthrough of the pack is a whole agentic turn
 * behind that: measured in production on 2026-09-14, `pendingPredictionId` was stamped ~2.2s after the
 * accept and the first words landed 27s after it. The SYSTEM row is invisible to clients, so for those
 * first seconds the chat has no messages, no stamp, and therefore nothing to render at all.
 *
 * That is what the accept landing's "Open chat" button dropped people into: a blank screen, reported as
 * "it opens a chat and shows nothing". So the button carries the marker below, and seeing it arms a
 * local busy state — the thinking loader shows from the first frame, and the real turn takes over the
 * moment the conversation carries a stamp or a row streams in.
 *
 * Same shape and the same reasoning as `lib/connect-handoff`, one flow over.
 */

/** The accept landing stamps this on the chat URL it opens. */
export const PACK_HANDOFF_PARAM = 'packPending';

/**
 * How long the accept alone is allowed to mean "busy".
 *
 * It bridges to a real signal, it does not replace one: an announcement that never comes (the receiver
 * was brand-new so their pack intro rides the onboarding greeting instead, the dispatch was dropped,
 * the service is down) must not leave a spinner running forever. Wider than the connect handoff's 45s
 * because the wait it covers is genuinely longer — the observed reply took 27s and that turn had to
 * read the pack manifest, the receipt and the receiving-packs skill before it could say anything.
 */
export const PACK_HANDOFF_GRACE_MS = 90 * 1000;

/** Whether an accept observed at `armedAt` still counts as a turn about to start. */
export function packHandoffPending(armedAt: number | null, now: number = Date.now()): boolean {
  if (armedAt === null) return false;
  // A clock-skewed future stamp is still live — only the past can expire.
  return now - armedAt < PACK_HANDOFF_GRACE_MS;
}

/** The chat URL the accept landing's CTA opens, marked so the chat knows a turn is on its way. */
export function packChatHref(conversationId: string | undefined): string {
  const params = new URLSearchParams();
  if (conversationId) params.set('conversation', conversationId);
  // Fullscreen, never bare `/`: `/` only opens the chat for a user still in onboarding state.
  params.set('chat', 'fullscreen');
  params.set(PACK_HANDOFF_PARAM, '1');
  return `/?${params.toString()}`;
}
